import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { useApp } from '@/lib/provider/AppProvider'
import { useAuth } from '@/lib/provider/AuthProvider'
import { FLAGS } from '@/lib/hooks/useFeatureFlag'
import { cn } from '@/lib/misc/Utils'

import UsersIcon from '@icons/users'

const CommandPalette = lazy(() => import('./CommandPalette'))

export const IMPERSONATION_LABEL = 'Switch customer'

const EXIT_DURATION = 140

export const useCanImpersonate = (): boolean => {
  const [appState] = useApp()
  const [auth] = useAuth()

  return useMemo(
    () =>
      Boolean(auth.user.show_company_info) &&
      Boolean(appState.waffleFlags?.includes(FLAGS.SUPERUSER_COMMAND_PALETTE)),
    [auth.user.show_company_info, appState.waffleFlags]
  )
}

interface ImpersonationButtonProps {
  className?: string
  activeClassName?: string
  iconClassName?: string
  children?: (label: string) => React.ReactNode
}

const ImpersonationButton = ({
  className,
  activeClassName,
  iconClassName,
  children,
}: ImpersonationButtonProps) => {
  const canImpersonate = useCanImpersonate()
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const exitTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

  const requestClose = useCallback(() => {
    setIsClosing(true)
    clearTimeout(exitTimeout.current)
    exitTimeout.current = setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
    }, EXIT_DURATION)
  }, [])

  const toggle = useCallback(() => {
    if (isOpen && !isClosing) {
      requestClose()
      return
    }
    clearTimeout(exitTimeout.current)
    setIsClosing(false)
    setIsOpen(true)
  }, [isClosing, isOpen, requestClose])

  useEffect(() => () => clearTimeout(exitTimeout.current), [])

  useEffect(() => {
    if (!canImpersonate) return

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        toggle()
      } else if (event.key === 'Escape' && isOpen && !isClosing) {
        requestClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [canImpersonate, isClosing, isOpen, requestClose, toggle])

  if (!canImpersonate) return null

  return (
    <>
      <button
        type="button"
        data-cy="impersonation-button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={isOpen && !isClosing}
        aria-keyshortcuts="Meta+K Control+K"
        title={IMPERSONATION_LABEL}
        className={cn(className, isOpen && !isClosing && activeClassName)}
      >
        {children ? (
          children(IMPERSONATION_LABEL)
        ) : (
          <>
            <UsersIcon className={cn('size-[18px]', iconClassName)} />
            <span className="sr-only">{IMPERSONATION_LABEL}</span>
          </>
        )}
      </button>

      {isOpen &&
        createPortal(
          <Suspense fallback={null}>
            <CommandPalette onClose={requestClose} isClosing={isClosing} />
          </Suspense>,
          document.body
        )}
    </>
  )
}

export default ImpersonationButton
